import { RefreshCw, AlertTriangle } from 'lucide-react';
import CodeEditor from '../ui/CodeEditor';
import BodyBeautifyButton from './BodyBeautifyButton';
import SyntaxHighlightInput from '../ui/SyntaxHighlightInput';
import { KvTable, KvRow } from '../ui/KvTable';
import OsScroll from '../ui/OsScroll';
import KeptTabPane from './KeptTabPane';
import FormDataKeyField from '../ui/FormDataKeyField';
import FormDataFileValueField from '../ui/FormDataFileValueField';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { codeEditorLanguageForBody, bodyPlaceholderKey } from '../../utils/requestBodyMode';
import GraphQLBodyEditor from '../ui/GraphQLBodyEditor';
import { normalizeGraphQLBody } from '../../utils/graphqlSchema';
import { REQUEST_BUILDER_KV_INPUT_CLASS, REQUEST_BUILDER_KV_INPUT_STYLE } from './requestBuilderLayout';

/** The Body tab: mode bar and body editor (plain render function, called where the markup was). */
export function renderRequestBodyPane({
  activeTab, bodyType, formDataFileInputRef, handleFormDataFileInput, handleBodyTypeChange, t,
  rawBodyLanguage, setRawBodyLanguage, notifyChange, body, setBody, gqlAutoFetch, setGqlAutoFetch,
  gqlSchemaStatus, doGqlIntrospect, url, gqlSchemaError, gqlSchema, splitLayoutTick,
  selectedEnvironment, globalVariables, collectionVariables, folderVariables, workspaceVariables,
  onVariableUpdate, initialData, currentWorkspaceId, formDataFields, updateFormDataFields,
  removeFormDataField, authVariableProps, pickFormDataFile, sendHttpRequest,
}) {
  return (
    <KeptTabPane active={activeTab === 'body'}>
        <div className="request-body-shell relative flex h-full min-h-0 flex-col">
          {bodyType === 'form-data' && (
            <>
              {/* Browser-only: programmatic file picker for form-data file rows (Tauri uses native dialog). */}
              <input
                ref={formDataFileInputRef}
                type="file"
                className="form-data-browser-file-input"
                aria-hidden
                tabIndex={-1}
                onChange={handleFormDataFileInput}
              />
            </>
          )}
          <div className="request-body-mode-bar flex items-center gap-2 mb-2 flex-shrink-0 px-5">
            <CompactSelectPicker
              value={bodyType}
              onChange={handleBodyTypeChange}
              className="body-mode-picker"
              menuMinWidth={120}
              options={[
                { value: 'none', label: t('request.bodyNone') },
                { value: 'raw', label: t('request.bodyRaw') },
                { value: 'form-data', label: t('request.bodyFormData') },
                { value: 'urlencoded', label: 'URL Encoded' },
                { value: 'graphql', label: t('request.bodyGraphQL') },
              ]}
              aria-label="Body type"
            />
            {bodyType === 'raw' && (
              <CompactSelectPicker
                value={rawBodyLanguage}
                onChange={(lang) => {
                  setRawBodyLanguage(lang);
                  notifyChange({ rawBodyLanguage: lang });
                }}
                className="body-mode-picker body-lang-picker"
                menuMinWidth={76}
                options={[
                  { value: 'text', label: t('request.bodyRawText') },
                  { value: 'json', label: t('request.bodyRawJson') },
                  { value: 'xml', label: t('request.bodyRawXml') },
                ]}
                aria-label={t('request.bodyRawLanguage')}
              />
            )}
            {bodyType === 'raw' && (
              <BodyBeautifyButton
                language={rawBodyLanguage}
                value={body}
                onChange={(next) => {
                  setBody(next);
                  notifyChange({ body: next });
                }}
              />
            )}
            {bodyType === 'graphql' && (
              <>
                <CompactSelectPicker
                  value={gqlAutoFetch ? 'auto' : 'none'}
                  onChange={(v) => setGqlAutoFetch(v === 'auto')}
                  options={[
                    { value: 'auto', label: 'Auto Fetch' },
                    { value: 'none', label: 'No schema' },
                  ]}
                  className="gql-schema-picker"
                  menuMinWidth={120}
                  aria-label="Schema fetch mode"
                />
                <button
                  type="button"
                  className={`gql-refresh-btn${gqlSchemaStatus === 'loading' ? ' gql-refresh-btn--spinning' : ''}`}
                  onClick={doGqlIntrospect}
                  disabled={!url?.trim() || gqlSchemaStatus === 'loading'}
                  title={url?.trim() ? 'Refresh schema' : 'Enter a URL first'}
                  aria-label="Refresh schema"
                >
                  <RefreshCw size={13} />
                </button>
                {gqlSchemaStatus === 'error' && (
                  <span
                    className="gql-schema-warning"
                    title={gqlSchemaError || 'Schema fetch failed'}
                    aria-label={`Schema error: ${gqlSchemaError}`}
                  >
                    <AlertTriangle size={13} />
                  </span>
                )}
              </>
            )}
          </div>
          <div className="request-body-pane flex-1 min-h-0 flex flex-col overflow-hidden">
          {bodyType === 'none' ? (
            <div className="request-body-none-state flex flex-1 items-center justify-center px-5">
              <p className="text-sm text-gray-500 dark:text-zinc-400">{t('request.bodyNoneHint')}</p>
            </div>
          ) : bodyType === 'graphql' ? (
            <GraphQLBodyEditor
              body={normalizeGraphQLBody(body)}
              schema={gqlSchema}
              schemaStatus={gqlSchemaStatus}
              schemaError={gqlSchemaError}
              onChange={(newBody) => {
                setBody(newBody);
                notifyChange({ body: newBody });
              }}
              splitLayoutTick={splitLayoutTick}
              selectedEnvironment={selectedEnvironment}
              globalVariables={globalVariables}
              collectionVariables={collectionVariables}
              folderVariables={folderVariables}
              workspaceVariables={workspaceVariables}
              onVariableUpdate={onVariableUpdate}
              variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
            />
          ) : bodyType === 'form-data' || bodyType === 'urlencoded' ? (
            <OsScroll className="request-body-scroll flex-1 min-h-0 px-5">
              <KvTable variant="formdata">
                {formDataFields.map((field, index) => (
                  <KvRow
                    key={field._id}
                    variant="formdata"
                    enabled={field.enabled !== false}
                    onToggleEnabled={(v) => updateFormDataFields(index, 'enabled', v)}
                    showRemove={!(index === formDataFields.length - 1 && field.key === '')}
                    onRemove={() => removeFormDataField(index)}
                    removeDisabled={formDataFields.length === 1}
                    renderKey={
                      <FormDataKeyField
                        fieldType={field.type}
                        onTypeChange={
                          bodyType === 'form-data'
                            ? (nextType) => updateFormDataFields(index, 'type', nextType)
                            : undefined
                        }
                        keyValue={field.key}
                        onKeyChange={(e) => updateFormDataFields(index, 'key', e.target.value)}
                        variableProps={authVariableProps}
                      />
                    }
                    renderValue={
                      field.type === 'file' && bodyType !== 'urlencoded' ? (
                        <FormDataFileValueField
                          field={field}
                          onPick={() => pickFormDataFile(index)}
                        />
                      ) : (
                        <SyntaxHighlightInput
                          placeholder={t('request.value')}
                          value={field.value}
                          onChange={(e) => updateFormDataFields(index, 'value', e.target.value)}
                          className={REQUEST_BUILDER_KV_INPUT_CLASS}
                          style={REQUEST_BUILDER_KV_INPUT_STYLE}
                          {...authVariableProps}
                        />
                      )
                    }
                  />
                ))}
              </KvTable>
            </OsScroll>
          ) : (
            <div className="request-body-scroll flex-1 min-h-0 overflow-hidden px-5">
              <CodeEditor
                splitLayoutTick={splitLayoutTick}
                hideChrome
                value={body}
                onChange={(newBody) => {
                  setBody(newBody);
                  notifyChange({ body: newBody });
                }}
                language={codeEditorLanguageForBody(bodyType, rawBodyLanguage)}
                placeholder={t(bodyPlaceholderKey(rawBodyLanguage))}
                height="100%"
                selectedEnvironment={selectedEnvironment}
                globalVariables={globalVariables}
                collectionVariables={collectionVariables}
                folderVariables={folderVariables}
                workspaceVariables={workspaceVariables}
                onVariableUpdate={onVariableUpdate}
                variableContext={{ collectionId: initialData?.collectionId, workspaceId: currentWorkspaceId }}
                onCtrlEnter={sendHttpRequest}
              />
            </div>
          )}
          </div>
        </div>
    </KeptTabPane>
  );
}
